import { RESOURCE_LABELS } from '@bienenkampf/shared';
import { Badge, Button, Card, Progress } from '@bienenkampf/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatAbsRel, formatDuration, formatNumber } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';
import type { HiveView } from './types.js';

/** Koeniginnen-Karte auf der Stockseite: regierende Koenigin, Reserve, Larven, Zucht, Weisellosigkeit. */
export function QueenCard({ view, now }: { view: HiveView; now: Date }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const q = view.queen;
  const breed = useMutation(
    trpc.hive.queen.breed.mutationOptions({
      onSettled: () =>
        void qc.invalidateQueries({ queryKey: trpc.hive.get.queryKey({ hiveId: view.id }) }),
    }),
  );
  const cell = view.buildings.find((b) => b.type === 'QUEEN_CELL')?.level ?? 0;
  const canAfford = (['nectar', 'pollen', 'wax'] as const).every(
    (r) => view.resources[r] >= q.breedCost[r],
  );
  const costText = (['nectar', 'pollen', 'wax'] as const)
    .filter((r) => q.breedCost[r] > 0)
    .map((r) => `${formatNumber(q.breedCost[r])} ${RESOURCE_LABELS[r]}`)
    .join(', ');

  return (
    <Card
      title="Königin"
      action={
        q.ruling ? (
          <Badge tone="amber">👑 {q.ruling.name}</Badge>
        ) : (
          <Badge tone="red">weisellos</Badge>
        )
      }
    >
      {q.ruling ? (
        <p className="text-sm text-stone-300">
          {q.ruling.name} regiert seit {formatAbsRel(q.ruling.bornAt, now)}.
        </p>
      ) : (
        <div className="rounded-md border border-red-900/60 bg-red-950/40 p-2 text-sm text-red-200">
          <p>
            Der Stock hat keine Königin: halbe Produktion ({Math.round(q.queenlessProdMult * 100)}{' '}
            %), keine Ausbildung.
          </p>
          {q.queenlessUntil && (
            <p className="mt-1 text-xs">
              Notkönigin schlüpft {formatAbsRel(q.queenlessUntil, now)} – dann geht ein Teil der
              Bienen verloren. Eine schlüpfende Larve oder eine gelieferte Reservekönigin übernimmt
              sofort.
            </p>
          )}
        </div>
      )}

      <h3 className="mt-3 text-sm font-medium">
        Reserve ({q.reserve.length + q.larvae.length}/{q.reserveCap})
      </h3>
      {q.reserve.length + q.larvae.length === 0 && (
        <p className="text-xs text-stone-400">
          {cell > 0
            ? 'Keine Reserve. Eine Reservekönigin rückt nach, wenn die regierende fällt.'
            : 'Baue eine Weiselzelle, um Königinnen zu züchten.'}
        </p>
      )}
      <ul className="flex flex-col gap-1 text-xs text-stone-300">
        {q.reserve.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-2">
            <span>♛ {r.name}</span>
            <span className="text-stone-500">bereit · seit {formatAbsRel(r.bornAt, now)}</span>
          </li>
        ))}
        {q.larvae.map((l) => {
          const start = l.bornAt.getTime();
          const end = l.hatchesAt ? l.hatchesAt.getTime() : start + 1;
          const p = Math.min(1, Math.max(0, (now.getTime() - start) / (end - start)));
          return (
            <li key={l.id}>
              <div className="flex items-center justify-between gap-2">
                <span>🥚 {l.name}</span>
                <span className="text-stone-500">
                  schlüpft {l.hatchesAt ? formatAbsRel(l.hatchesAt, now) : '–'}
                </span>
              </div>
              <Progress value={p} className="mt-1" tone="amber" />
            </li>
          );
        })}
      </ul>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={!q.canBreed || !canAfford || breed.isPending}
          loading={breed.isPending}
          onClick={() => breed.mutate({ hiveId: view.id })}
          title={
            cell === 0
              ? 'Weiselzelle nötig'
              : !q.canBreed
                ? 'Weiselzelle voll'
                : !canAfford
                  ? 'Nicht genug Ressourcen'
                  : undefined
          }
        >
          Weisellarve ansetzen
        </Button>
        <span className="text-xs text-stone-400">
          {costText} · {formatDuration(q.breedSeconds)}
        </span>
      </div>
      {breed.error && <p className="mt-2 text-sm text-red-400">{breed.error.message}</p>}
    </Card>
  );
}
